/**
 * StoryMapSVG — Phase 1, Component 4 of the TeachBud Representation Catalog
 *
 * Renders a story map (narrative organizer) per §5.2 Reading Comprehension — Narrative:
 *   - K–1: Four boxes 2×2. Labels: Characters | Setting | Problem | Solution
 *   - Grade 2–3: Five boxes in sequence order (adds "Events")
 *   - Grade 4–5: Five boxes with more writing space
 *   - Student mode: empty boxes with ruled lines
 *   - Teacher mode: content provided per box
 *   - Grades K–5 ELA
 */

import React from 'react';

export interface StoryMapProps {
  /** Use 4-box (K–1) or 5-box (2–5) layout. Default auto from grade. */
  boxCount?: 4 | 5;
  /** Content for each box in teacher mode. */
  boxContent?: (string | null)[];
  /** Custom box labels. Default: Characters / Setting / Problem / Events / Solution. */
  boxLabels?: string[];
  /** When true, boxes are empty with ruled lines. */
  studentMode?: boolean;
  /** Number of ruled lines per box. Default auto from grade. */
  linesPerBox?: number;
  /** Override pixel width. */
  width?: number;
  /** Grade (0=K). */
  grade?: number;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W = 540;
const PAD  = 14;

// K–1 uses a 2×2 grid; Grade 2+ uses a horizontal chain
const LABELS_4 = ['Characters', 'Setting', 'Problem', 'Solution'];
const LABELS_5 = ['Characters', 'Setting', 'Problem', 'Events', 'Solution'];

// Colors per subject (ELA = violet)
const HEADER_FILL  = '#7C3AED';  // violet-600
const HEADER_LIGHT = '#F5F3FF';  // violet-50
const BOX_STROKE   = '#DDD6FE';  // violet-200
const LINE_COLOR   = '#E5E7EB';
const TEXT_COLOR   = '#1E3A5F';

export function StoryMapSVG({
  boxCount,
  boxContent,
  boxLabels,
  studentMode = true,
  linesPerBox,
  width,
  grade = 2,
  className = '',
}: StoryMapProps) {
  const nBoxes = boxCount ?? (grade <= 1 ? 4 : 5);
  const labels = boxLabels ?? (nBoxes === 5 ? LABELS_5 : LABELS_4);
  const linesN  = linesPerBox ?? (grade <= 1 ? 2 : grade <= 3 ? 3 : 4);

  // K–1: 2×2 grid layout; 2+: 5 horizontal boxes (or 2 rows of 2+3)
  const useGrid = grade <= 1 && nBoxes === 4;

  const HEADER_H = 28;
  const BOX_H    = grade <= 1 ? 100 : grade <= 3 ? 88 : 80;
  const LINE_SPACING = Math.floor((BOX_H - HEADER_H - 14) / linesN);

  let VB_H: number;
  let boxes: { x: number; y: number; w: number; label: string; content: string | null }[] = [];

  if (useGrid) {
    const colW = (VB_W - 3 * PAD) / 2;
    VB_H = PAD + 2 * BOX_H + PAD + PAD;
    const positions = [
      { x: PAD,            y: PAD },
      { x: PAD + colW + PAD, y: PAD },
      { x: PAD,            y: PAD + BOX_H + PAD },
      { x: PAD + colW + PAD, y: PAD + BOX_H + PAD },
    ];
    boxes = labels.slice(0, 4).map((lbl, i) => ({
      x: positions[i].x, y: positions[i].y,
      w: colW, label: lbl,
      content: !studentMode ? (boxContent?.[i] ?? null) : null,
    }));
  } else {
    // Horizontal: 5 equal boxes in a row (may wrap to 2 rows)
    const twoRow = nBoxes === 5;
    const row1Count = twoRow ? 3 : nBoxes;
    const row2Count = twoRow ? 2 : 0;
    const boxW1 = (VB_W - (row1Count + 1) * PAD) / row1Count;
    const boxW2 = row2Count > 0 ? (VB_W - (row2Count + 1) * PAD) / row2Count : 0;
    VB_H = PAD + (twoRow ? 2 : 1) * BOX_H + (twoRow ? PAD : 0) + PAD;

    for (let i = 0; i < row1Count; i++) {
      boxes.push({ x: PAD + i * (boxW1 + PAD), y: PAD, w: boxW1, label: labels[i], content: !studentMode ? (boxContent?.[i] ?? null) : null });
    }
    for (let i = 0; i < row2Count; i++) {
      const startX = (VB_W - row2Count * boxW2 - (row2Count - 1) * PAD) / 2;
      boxes.push({ x: startX + i * (boxW2 + PAD), y: PAD + BOX_H + PAD, w: boxW2, label: labels[row1Count + i], content: !studentMode ? (boxContent?.[row1Count + i] ?? null) : null });
    }
  }

  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;
  const HEADER_FS = grade <= 2 ? 12 : 10;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW} height={svgH}
      className={className}
      role="img"
      aria-label={`Story map with ${nBoxes} sections`}
    >
      <title>Story map</title>

      {boxes.map(({ x, y, w, label, content }, i) => {
        const bodyY = y + HEADER_H;
        const bodyH = BOX_H - HEADER_H;

        return (
          <g key={i}>
            {/* Box shadow */}
            <rect x={x + 2} y={y + 2} width={w} height={BOX_H} rx={8} fill="#E9D5FF" opacity={0.5} />

            {/* Box body */}
            <rect x={x} y={bodyY} width={w} height={bodyH} fill={HEADER_LIGHT} stroke={BOX_STROKE} strokeWidth={1.5} />

            {/* Header */}
            <rect x={x} y={y} width={w} height={HEADER_H} rx={8} fill={HEADER_FILL} />
            <rect x={x} y={y + HEADER_H - 8} width={w} height={9} fill={HEADER_FILL} />

            {/* Header label */}
            <text
              x={x + w / 2} y={y + HEADER_H / 2 + 5}
              textAnchor="middle"
              fontSize={HEADER_FS}
              fontFamily="Nunito, sans-serif"
              fontWeight={700}
              fill="#FFFFFF"
            >
              {label}
            </text>

            {/* Content or ruled lines */}
            {content ? (
              <text x={x + 6} y={bodyY + 16} fontSize={9} fontFamily="Inter, sans-serif" fill={TEXT_COLOR}>
                {content.slice(0, 40)}
              </text>
            ) : (
              Array.from({ length: linesN }).map((_, li) => {
                const ly = bodyY + 12 + li * LINE_SPACING + LINE_SPACING / 2;
                if (ly > y + BOX_H - 6) return null;
                return (
                  <line key={li} x1={x + 8} y1={ly} x2={x + w - 8} y2={ly} stroke={LINE_COLOR} strokeWidth={1} />
                );
              })
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function parseStoryMapHint(hint: string): StoryMapProps {
  const is5box = /five|5\s*box|grade\s*[2-9]|events/i.test(hint);
  return { boxCount: is5box ? 5 : 4, studentMode: true };
}
